<script setup>
  import { ref, getCurrentInstance, computed, onMounted } from 'vue'
  import { useRouter, useRoute } from 'vue-router';
  import { usePageLoadingStore } from '../../../../stores/pageLoading';
  import { useUserStore } from '../../stores/user';
  import { useNavigationStore } from '../../../../stores/navigation';
  import { storeToRefs } from "pinia";
  import mypageJobFairResumeService from '../../services/mypage/jobFair/mypageJobFairResumeService';
  import mypageJobFairService from '../../services/mypage/jobFair/mypageJobFairService';
  import SearchResidenceModal from "@/pages/user/components/modal/matching/SearchResidenceModal.vue";
  import JobFairResumeDetailModal from '../../components/modal/jobFair/JobFairResumeDetailModal.vue';
  import JobFairReusmeCreatePolicyModal from '../../components/modal/jobFair/JobFairReusmeCreatePolicyModal.vue';
  import JobFairIntvProposalJobNoticeListModal from '../../components/modal/jobFair/JobFairIntvProposalJobNoticeListModal.vue';
  import moment from 'moment';
  
  const navigationStore = useNavigationStore();
  const loadingStore = usePageLoadingStore();
  const userStore = useUserStore();
  const { user, isAuthenticated } = storeToRefs(userStore);

  const userGb = computed(() => user.value?.userGb);
  const userCorpMasterYn = computed(() => user.value?.userInfo.corpMasterYn);

  const globals = getCurrentInstance().appContext.config.globalProperties;

  const { alert, confirm, $pageListBoardNo } = globals;

  const router = useRouter();
  const route = useRoute();

  const interestsOptions = ref(globals.$interests ? globals.$interests.map(item => ({ ...item })) : []);
  const schoolGbOptions = ref(globals.$schoolGb ? globals.$schoolGb.map(item => ({ ...item })) : []);
  const eduTenureGbOptions = ref(globals.$eduTenureGb ? globals.$eduTenureGb.map(item => ({ ...item })) : []);
  const majorGbOptions = ref(globals.$majorGb ? globals.$majorGb.map(item => ({ ...item })) : []);
  const searchMonthOptions = ref(globals.$searchMonths ? globals.$searchMonths.map(item => ({ ...item })) : []);

  const searchOpen = ref(true);
  const searchResidenceModal = ref(null);

  const interestGbListRef = ref(null);
  const schoolGbListRef = ref(null);
  const eduTenureGbListRef = ref(null);
  const majorGbListRef = ref(null);

  const isResumeResOpenYn = ref('N'); // 학생일때, 공개된 이력서가 있는지 여부

  const initialSearchInput = {
    searchMonthType: searchMonthOptions.value[0] ? searchMonthOptions.value[0].value : 1,
    searchResidences: [],
    searchInterests: interestsOptions.value.map(item => item.value),
    searchSchoolGbs: schoolGbOptions.value.map(item => item.value),
    searchEduTenureGbs: eduTenureGbOptions.value.map(item => item.value),
    searchMajorGbs: majorGbOptions.value.map(item => item.value),
  };
  const searchInput = ref({ ...initialSearchInput });

  const spreadMajorGbClass = ref('min');

  const showSearchResidenceModal = () => {
    searchResidenceModal.value.show(searchInput.value.searchResidences);
  };

  const handleSelectionSubmit = (selectedResult) => {
    searchInput.value.searchResidences = selectedResult;
  };

  const deleteResidence = (residence) => {
    searchInput.value.searchResidences = searchInput.value.searchResidences.filter(
      (item) => item !== residence
    );
  };

  const spreadMajorGb = () => {
    spreadMajorGbClass.value = spreadMajorGbClass.value === 'min' ? '' : 'min';
  };

  const clearSearch = () => {
    Object.assign(searchInput.value, initialSearchInput);

    if (interestGbListRef.value && interestGbListRef.value.initOptions) {
      interestGbListRef.value.initOptions();
    }
    if (schoolGbListRef.value && schoolGbListRef.value.initOptions) {
      schoolGbListRef.value.initOptions();
    }
    if (eduTenureGbListRef.value && eduTenureGbListRef.value.initOptions) {
      eduTenureGbListRef.value.initOptions();
    }
    if (majorGbListRef.value && majorGbListRef.value.initOptions) {
      majorGbListRef.value.initOptions();
    }
  };

  const onClickSearch = () => {
    currentPage.value = 1;
    retrieveList();
  };

  const currentPage = ref(1);
  const pageSize = ref(10);
  const totalPages = ref(0);
  const totalCount = ref(0);
  const list = ref([]);

  const jobFairResumeModal = ref(null); // 특별채용관 이력서
  const intvProposalJobNoticeListModal = ref(null); // 면접제안
  const jobFairReusmeCreatePolicyModal = ref(null); // 개인정보 이용 수집 동의

  const pageSizeGroup = ref([
    { label:"10개씩 보기", value: "10" },
    { label:"30개씩 보기", value: "30" },
    { label:"50개씩 보기", value: "50" },
    { label:"100개씩 보기", value: "100" },
  ]);

  const retrieveList = () => {
    const params = {
      searchMonths: searchInput?.value.searchMonthType ?? 1,
      searchResidences: searchInput?.value.searchResidences?.join(',') || "", // "회로/시스템 설계,장비 운영/유지보수/개선,장비/부품 개발,소재 개발 및 평가,공정 개발 및 평가,패키지 개발,제품 평가 및 분석,반도체 SW개발,기타",
      searchInterests: searchInput?.value.searchInterests?.join(',') || "", // "신입,경력,경력무관",
      searchSchoolGbs: searchInput?.value.searchSchoolGbs?.join(',') || "", // "01,02,03,04,05",
      searchEduTenureGbs: searchInput?.value.searchEduTenureGbs?.join(',') || "", // "정규직,계약직,인턴,프리랜서",
      searchMajorGbs: searchInput?.value.searchMajorGbs?.join(',') || "", // "서울특별시 전체, 경기도 성남시",
      pageNumber: currentPage.value,
      size: pageSize.value,
      userId: user.userId,
    };

    loadingStore.loading = true;
    if(userGb.value === 'COMPANY') {
      mypageJobFairResumeService.retrieveAllResumePaging(params)
        .then(res => {
          if(res.data.code.toLowerCase() === 'success') {
            const result = res.data.result;
            totalPages.value = result.totalPages;
            totalCount.value = result.totalElements;
            list.value = $pageListBoardNo(result.content, totalCount.value, pageSize.value, currentPage.value);
          }
          loadingStore.loading = false;
        })
        .catch( (error) =>  {
          loadingStore.loading = false;
        });
    } else {
      mypageJobFairResumeService.retrieveAllResumePagingWithMasking(params)
      .then(res => {
        if(res.data.code.toLowerCase() === 'success') {
          const result = res.data.result;
          totalPages.value = result.totalPages;
          totalCount.value = result.totalElements;
          list.value = $pageListBoardNo(result.content, totalCount.value, pageSize.value, currentPage.value);
        }
        loadingStore.loading = false;
      })
      .catch( (error) =>  {
        loadingStore.loading = false;
      });
    }
  };

  const onSelectPage = (pageNo) => {
    currentPage.value = pageNo;
    retrieveList();
  };

  const onSelectPageSize = (option) => {
    pageSize.value = option.value;
    currentPage.value = 1;
    retrieveList();
  };

  const onClickEntryResume = async (to) => {
    if(!isAuthenticated.value) {
      alert("로그인 후 이용해주세요.");
      navigationStore.lastAccessPage = route.fullPath;
      router.push({name: 'Signin'});
    } else {
      if (userGb.value === 'STUDENT') {
        if(to === 'mypage') {
          router.push({ name: 'MyPageJobFair', query : { tab: 'resume'}});
        } else {
          const userId = user.value.userInfo.userCd;

          loadingStore.loading = false;
          mypageJobFairService.retrieveRecruitPrivacyAgreeYnByUserId(userId)
            .then( async (res) => {
              if(res.data.code.toLowerCase() === 'success') {
                const result = res.data.result;

                if (result !== 'Y') { // 개인정보 이용 동의 내역이 없을때
                  const ok = await jobFairReusmeCreatePolicyModal.value.show();
                  if(ok) {
                    router.push({ name: 'MyPageJobFairResumeCreate' });
                  }
                } else {
                  router.push({ name: 'MyPageJobFairResumeCreate' });
                }
              }
            })
            .catch( (error) =>  {
              loadingStore.loading = false;
            });
        }
      } else {
        alert(["학생/취업준비생 회원만", "이력서 등록이 가능합니다."]);
      }
    }
  }

  // title에 '*'이 포함되어 있는지 확인
  const hasAsterisk = (title) => title.includes('*');

  // 이력서 보기
  const onClickResumeTitle = async (talent) => {
    const gnbResume = 'gnbResume'; // 삭제버튼 숨기기 위한 데이터
    if (userGb.value === 'COMPANY') {
      if (userCorpMasterYn.value === 'Y') {
        const result = await jobFairResumeModal.value.show({
          resumeId: talent.resumeId,
          interestYn: talent.interestYn,
        });

        if(result && result.interestYn) {
          talent.interestYn = result.interestYn;
        }
      } else {
        alert("재직자/기업 마스터 회원만 이력서 확인이 가능합니다.");
      }
    } else { // 학생이면서 내 이력서일 경우
      const result = await jobFairResumeModal.value.show({
        resumeId: talent.resumeId,
        interestYn: talent.interestYn,
      });

      if(result && result.interestYn) {
        talent.interestYn = result.interestYn;
      }
    }
  }
  
  // 관심인재
  const onClickInterestYn = async (resumeId, interestYn) => {
    let statusName = interestYn === 'Y' ? '삭제' : '등록';
    const ok = await confirm([`관심인재를 ${statusName}하시겠습니까?`], `관심인재 ${statusName}`);

    if(ok) {
      const paramObj = {
        resumeId: resumeId,
        interestYn: interestYn === 'Y' ? "N" : "Y",
      }
      mypageJobFairService.updateCorpInterestHallEntryYn(paramObj)
        .then( (res) => {
          loadingStore.loading = false;
          if(res.data.code.toLowerCase() === 'success') {
            //const result = res.data.result;
            alert(`${statusName}하였습니다.`);
            retrieveList();
          } else {
            alert(`${statusName}를 실패하였습니다.`);
          }
        }).catch( (res) =>  {
          loadingStore.loading = false;
          alert(`${statusName}를 실패하였습니다.`);
        });
    }
  }

  // 면접제안
  const onClickInterviewProposal = async (resumeId) => {
    const result = await intvProposalJobNoticeListModal.value.show({
      corpId: user?.value?.userInfo?.corporation?.corpId || '',
      resumeId,
    });

    if(result && result.jobNoticeSeq) {
      const paramObj = {
        resumeId,
        jobNoticeSeq: result.jobNoticeSeq,
      };

      loadingStore.loading = true;
      mypageJobFairService.createInterviewProposalEntry(paramObj)
        .then( (res) => {
          loadingStore.loading = false;
          if(res.data.code.toLowerCase() === 'success') {
            alert(["면접제안을 하였습니다."]);
          } else {
            alert(["면접제안을 실패하였습니다."]);
          }
        })
        .catch( (error) =>  {
          alert(["면접제안을 실패하였습니다."]);
          loadingStore.loading = false;
        }); 
    }
  }

  // 엑셀
  const downloadUserResumeList = async () => {
    const ok = await confirm(["인재정보 목록을 내 PC로 다운로드할까요?"], "다운로드");
    if(ok) {
      const params = {
        searchMonths: searchInput?.value.searchMonthType ?? 1,
        searchResidences: searchInput?.value.searchResidences?.join(',') || "", // "회로/시스템 설계,장비 운영/유지보수/개선,장비/부품 개발,소재 개발 및 평가,공정 개발 및 평가,패키지 개발,제품 평가 및 분석,반도체 SW개발,기타",
        searchInterests: searchInput?.value.searchInterests?.join(',') || "", // "신입,경력,경력무관",
        searchSchoolGbs: searchInput?.value.searchSchoolGbs?.join(',') || "", // "01,02,03,04,05",
        searchEduTenureGbs: searchInput?.value.searchEduTenureGbs?.join(',') || "", // "정규직,계약직,인턴,프리랜서",
        searchMajorGbs: searchInput?.value.searchMajorGbs?.join(',') || "", // "서울특별시 전체, 경기도 성남시",
        pageNumber: currentPage.value - 1,
        size: pageSize.value,
        userId: user.userId,
      };

      loadingStore.loading = true;
      mypageJobFairResumeService.retrieveAllResumeExcel(params)
        .then( (res) => {
          loadingStore.loading = false;
          try {
            const href = URL.createObjectURL(res.data);

            // create "a" HTML element with href to file & click
            const link = document.createElement('a');
            link.href = href;
            link.setAttribute('download', `${moment().format("YYYYMMDD")}_인재정보리스트.xlsx`); //or any other extension
            document.body.appendChild(link);
            link.click();

            // clean up "a" element & remove ObjectURL
            document.body.removeChild(link);
            URL.revokeObjectURL(href);
          } catch(e) {
            console.error('다운로드 파일 처리 중 오류가 발생했습니다:', e);
          }
        }).catch( (err) =>  {
          loadingStore.loading = false;
          let errorMessage = '다운로드 데이터 생성 중 오류가 발생했습니다.';
          // Blob 응답 처리
          err.response.data.text()  // Blob을 텍스트로 변환
            .then(text => {
              try {
                // 텍스트를 JSON으로 파싱
                const responseData = JSON.parse(text);
                // 메시지 추출
                if (responseData?.result?.message) {
                  errorMessage = responseData.result.message;
                }
              } catch (parseError) {
                console.error('에러 메시지 파싱 중 오류가 발생했습니다.:', parseError);
              }
              // Toast에 에러 메시지 표시
              $toast('error', errorMessage);
            });
        });
    }
  }

  onMounted(() => {
    retrieveList();

    if(user?.value?.userGb === 'STUDENT') {
      mypageJobFairResumeService.retrieveResumeResOpenYn(user.value.userCd)
        .then( (res) => {
          if(res.data.code.toLowerCase() === 'success') {
            isResumeResOpenYn.value = res.data.result;
          }
        })
        .catch( (error) =>  {
          console.error('인재정보 공개여부 체크 중 오류가 발생했습니다:', error);
        });

    }
  });
</script>

<template>
  <div>
    <div class="process-wrap matching">
      <h4>이력서 등록 및 입사지원</h4>
      <div class="process-layout">
        <div class="process-steps-scroll">
          <ul>
            <li>- 이력서를 등록하고 나에게 딱 맞는 디스플레이 기업을 찾아보세요.</li>
            <li>- ‘인재정보‘에 이력서를 공개하고 기업 인사 담당자에게 면접 제안을 받아보세요.</li>
          </ul>
          <div class="process-steps">
            <div class="step"><div>이력서<br>등록</div></div>
            <div class="step-arrow"><div></div></div>
            <div class="step"><div>인재정보<br>공개</div></div>
            <div class="step-arrow"><div></div></div>
            <div class="step"><div>면접제안<br>확인/수락</div></div>
            <div class="step-arrow"><div></div></div>
            <div class="step"><div>매칭 완료</div></div>
            <div class="step-arrow active"><div></div></div>
            <div class="step large active"><div>기업별<br>채용 전형 진행</div></div>
          </div>
        </div>
        <div class="button-with-info">
          <p>인재정보에 이력서를 등록하고<br/> 실시간 채용제안을 받아보세요.</p>
          <div class="button-with-info-cta" @click="onClickEntryResume()">이력서 등록하기<span class="icon-button-right"></span></div>
        </div>
      </div>
    </div>
    <div class="filter-wrap mt-sm" :class="{'open': searchOpen}">
      <p @click="searchOpen = !searchOpen">검색</p>
      <div class="filter">
        <dl>
          <dt>등록(공개)일</dt>
          <dd>
            <template v-for="group in searchMonthOptions" :key="group.value">
              <KSARadioButton class="mr-md" name="searchMonthGb" :label="group.label" v-model="searchInput.searchMonthType" :value="group.value" />
            </template>
          </dd>
        </dl>
        <dl>
          <dt>거주 지역</dt>
          <dd class="residence-selected">
            <button @click="showSearchResidenceModal()"><i>+</i>지역 선택</button>
            <span v-for="option in searchInput.searchResidences" :key="option">
              {{ option }}<i @click="deleteResidence(option)">삭제</i>
            </span>
          </dd>
        </dl>
        <dl>
          <dt>관심 카테고리</dt>
          <dd>
            <KSACheckBoxGroup
              ref="interestGbListRef"
              className="mr-md"
              name="searchInterests"
              v-model="searchInput.searchInterests"
              :options="interestsOptions" />
          </dd>
        </dl>
        <dl>
          <dt>최종학력</dt>
          <dd>
            <KSACheckBoxGroup
              ref="schoolGbListRef"
              className="mr-md"
              name="searchSchoolGbs"
              v-model="searchInput.searchSchoolGbs"
              :options="schoolGbOptions" />
          </dd>
        </dl>
        <dl>
          <dt>최종학력상태</dt>
          <dd>
            <KSACheckBoxGroup
              ref="eduTenureGbListRef"
              className="mr-md"
              name="searchEduTenureGbs"
              v-model="searchInput.searchEduTenureGbs"
              :options="eduTenureGbOptions" />
          </dd>
        </dl>
        <dl>
          <dt>전공계열(주전공)</dt>
          <dd class="majorGb" :class="spreadMajorGbClass">
            <button @click="spreadMajorGb">{{ searchInput.searchMajorGbs.length}}개 <i></i></button>
            <KSACheckBoxGroup
              ref="majorGbListRef"
              className="mr-md"
              name="searchMajorGbs"
              v-model="searchInput.searchMajorGbs"
              :options="majorGbOptions" />
          </dd>
        </dl>
        <div class="btn-wrap">
          <KSASecondaryButton class="gray_button" text="초기화" @click="clearSearch" />
          <KSAPrimaryButton text="검색" @click="onClickSearch" />
        </div>
      </div>
    </div>
    <div class="info-box" v-if="user.userGb === 'STUDENT' && isResumeResOpenYn !== 'Y'">
      <p>‘인재정보’에 공개된 이력서가 없습니다. 공개할 이력서를 등록 및 설정해 주세요.</p>
      <KSAPrimaryButton text="이력서 관리(공개설정)" @click="onClickEntryResume('mypage')" />
    </div>
    <div class="list-top">
      <p>인재정보 <span>{{ $number(totalCount)}}</span>개</p>
      <div class="list-top mb-n-xs" style="margin-left: auto;">
        <KSAPrimaryButton v-show="userGb === 'COMPANY' && userCorpMasterYn === 'Y'" text="이력서 다운로드" @click="downloadUserResumeList()" class="mr-xs" />
        <KSASelect :options="pageSizeGroup" class="select" @selectOption="onSelectPageSize"/>
      </div>
    </div>
    <div class="table-form">
      <table class="table-form board-mobile-vertical">
        <tr>
          <th>번호</th>
          <th>이름</th>
          <th>이력서 제목</th>
          <th>관심 카테고리</th>
          <th>전공계열(주전공)</th>
          <th>등록(공개)일</th>
          <th v-show="userGb === 'COMPANY' && userCorpMasterYn === 'Y'">면접제안</th>
          <th v-show="userGb === 'COMPANY' && userCorpMasterYn === 'Y'">관심인재</th>
        </tr>
        <tr v-for="talent in list">
          <td>{{ talent.boardNo }}</td>
          <td>
            <span v-if="userGb === 'COMPANY'">{{ talent.userName }}</span>
            <span v-else>
              <template v-if="user.userName === talent.userName">{{ talent.userName }}</template>
              <template v-else>{{ talent.userName.substring(0, 1) + '**' }}</template>
            </span>
          </td>
          <td class="text-overflow long-overflow">
            <template v-if="userGb === 'COMPANY'">
              <p v-if="hasAsterisk(talent.title)">
                <span class="clickable">{{ talent.title }}</span>
              </p>
              <div v-else>
                <p class="file" v-if="talent.fileYn === 'Y'" v-for="resumeAttaches in talent.resumeAttaches" @click="$downloadForPrivate({fileName:resumeAttaches.fileName, fileDir: resumeAttaches.fileDir })">
                  {{ resumeAttaches.fileName }}
                </p>
                <p v-else @click="onClickResumeTitle(talent)">
                  <span class="clickable">{{ talent.title }}</span>
                </p>
              </div>
            </template>
            <template v-else>
              <p v-if="user.userName === talent.userName" @click="onClickResumeTitle(talent)">
                <span class="clickable">{{ talent.title }}</span>
              </p>
              <p v-else>***************</p>
            </template>
          </td>
          <td>{{ talent.interests }}</td>
          <td>{{ $gbNameKR("MAJOR_GB", talent.majorGb) }}</td>
          <td>{{ $yyyymmddHHmmss(talent.resOpenDate) }}</td>
          <td v-show="userGb === 'COMPANY' && userCorpMasterYn === 'Y'">
            <KSATextButton text="면접제안" @click="onClickInterviewProposal(talent.resumeId)" /> 
          </td>
          <td v-show="userGb === 'COMPANY' && userCorpMasterYn === 'Y'">
            <KSATextButton text="♥" v-if="talent.interestYn === 'Y'" @click="onClickInterestYn(talent.resumeId, talent.interestYn)" class="red_button" />
            <KSATextButton text="♡" v-else @click="onClickInterestYn(talent.resumeId, talent.interestYn)" class="red_button" />
          </td>
        </tr>
      </table>
    </div>
    <div class="result-none lecture-none" v-if="list.length === 0">
      등록된 인재정보가 없습니다.
    </div>
    <Paginate
        v-if="list.length > 0"
        v-model="currentPage"
        :page-count="totalPages"
        :click-handler="onSelectPage"
    />
    <SearchResidenceModal ref="searchResidenceModal" @submitSelection="handleSelectionSubmit" />
    <JobFairResumeDetailModal ref="jobFairResumeModal"/> <!-- 특별채용관 이력서 상세 -->
    <JobFairIntvProposalJobNoticeListModal ref="intvProposalJobNoticeListModal" />
    <JobFairReusmeCreatePolicyModal ref="jobFairReusmeCreatePolicyModal" />
  </div>
</template>
<style lang="scss" scoped>
@import "../../../../assets/scss/theme";

.info-box {
  display: flex;
  background: $gray-light_color;
  justify-content: center;
  align-items: center;
  gap: 32px;
  margin-block: 16px;
  padding: 16px;
}

.filter-wrap .filter dl dd {
  width: calc(100% - 200px);
  padding: 10px 20px 10px 0;
  display: flex;
  flex-wrap: wrap;
}

.majorGb button {
  right: 0;
}


.corp-wrap {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 22px;
  dl {
    flex: 1 1 calc(25% - 22px);
    max-width: calc(25% - 16px);
    height: 200px;
    border: 1px solid $gray-regular_color;
    border-radius: 6px;
    padding: 16px;
    cursor: pointer;
    dt {
      width: 100%;
      height: 70px;
      border: 1px solid $gray-regular_color;
    }
    dd {
      position: relative;
      margin-top: 10px;
      .text-over {
        display: block;
      }
      h1 {
        font-size: 18px;
      }
      p {
        max-width: 100%;
        &.link {
          color: $blue_color;
        }
        &:last-of-type {
          font-size: 14px;
        }
      }
      button {
        position: absolute;
        top: 0;
        right: 0;
        font-size: 20px;
      }
    }
  }
}

.filter-wrap>.filter>dl>dd.majorGb {
  padding-right: 100px;
  display: flex;
  flex-wrap: wrap;
}
</style>